<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Listbox from 'primevue/listbox';
import Menu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import {
  useEndpoint,
  useFetch,
  useOpportunityAssociations,
  useStringFunctions,
  useUtilities,
  useIndustryCodes,
} from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import type {
  ContactLabelType,
  DetailEditorContext,
  AssociationAccount,
  AssociationContact,
  AssociationsModel,
  AssociationsResponse,
  ContactLabel,
  GenericApiResponse,
  OpportunityType,
  OpportunityUpdatePayload,
} from '@apparatix/types/Leads';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import Modal from '@apparatix/Components/Modal.vue';
import AssociationListItem from './AssociationListItem.vue';
import AssociationListContainer from './AssociationListContainer.vue';
import AccountLabelTag from './AccountLabelTag.vue';
import ContactTypeChip from './ContactTypeChip.vue';
import Contacts from '@apparatix/leads/creation-wizard/Contacts.vue';
import Accounts from '@apparatix/leads/creation-wizard/Accounts.vue';
import { useOpportunityCreationStore } from '@apparatix/stores/useOpportunityCreationStore';
import { cloneDeep } from 'lodash';
import { Section } from '@apparatix/library/core';
import { button as greenButtonTheme } from '@apparatix/theme/Green';
import type { DetailTabName, FlyoutNavigationContext, WithNavigation } from './types';

interface AssociationsEditorProps {
  type: OpportunityType;
  context: DetailEditorContext;
  opportunityId?: number;
  tabId?: DetailTabName;
}

type Emits = WithNavigation<{
  (e: 'on:campaign-accounts-loaded', hasAny: boolean): void;
  (e: 'on:campaign-contacts-loaded', hasAny: boolean): void;
  (e: 'post-activity', payload: { modelObject: ModelObject, typeId: number }): void;
}>;

type ModelObject = {
  id: number;
  name: string;
  view: string;
};

const props = defineProps<AssociationsEditorProps>();

const emit = defineEmits<Emits>();

const model = defineModel<AssociationsModel>();
const accounts = ref<AssociationAccount[]>([]);
const contacts = ref<AssociationContact[]>([]);

const { industryCodeRequired } = useIndustryCodes();

const store = useOpportunityStore();
const confirm = useConfirm();
const { destroy } = useFetch();

const { canAssociateMoreAccounts } = useOpportunityAssociations(props.type, accounts, contacts);
const { isEmptyString } = useStringFunctions();
const { arrayHasValues } = useUtilities();

const showAddContactModal = ref(false);
const showContactTypeSelectDialog = ref(false);
const areContactsDirty = ref(false);
const contactMenuItems = ref<MenuItem[]>([
  {
    label: 'Edit contact types',
    icon: 'pi pi-tags',
    command: () => {
      showContactTypeSelectDialog.value = true;
    },
  },
  {
    label: 'Unassociate contact',
    icon: 'pi pi-user-minus',
    command: () => {
      showRemoveContactConfirm();
    },
  },
]);
const contactMenuRef = ref();
const activeContact = ref<AssociationContact>();

const showAssociateAccountsModal = ref(false);
const areAccountsDirty = ref(false);

watch(accounts, () => {
  if (!model?.value?.accounts) return;
  model.value.accounts = accounts.value;
}, { deep: true });

watch(contacts, () => {
  if (!model?.value?.contacts) return;
  model.value.contacts = contacts.value;
}, { deep: true });

const showRemoveContactConfirm = () => {
  if (props.type !== 'Lead' && activeContact.value.contactLabels.some(contact => contact.contactLabelId === 1)) {
    confirm.require({
      header: 'Notice',
      message: `There must be a primary contact associated with this ${props.type}.`,
      group: 'primaryContactSaveConfirm',
      acceptLabel: 'Ok',
      rejectClass: '!hidden',
    });
    return;
  }
  confirm.require({
    header: 'Confirm',
    group: 'removeContactConfirm',
    message: `Are you sure you want to remove this contact from this ${props.type}?`,
    acceptLabel: 'Yes',
    rejectLabel: 'Cancel',
    accept: async () => {
      const idx = contacts.value?.findIndex(contact => contact.id === activeContact.value.id);
      if (idx !== -1) {
        contacts.value.splice(idx, 1);
      }

      await destroy(`/crm/opportunities/${props.opportunityId}/contact/${activeContact.value.id}`);
    },
  });
};

const endpointUrl = computed(() => {
  if (!props.opportunityId) return '';
  return `/crm/opportunities/${props.opportunityId}/associations`;
});

const contactsExcludingActive = computed(() => {
  if (!activeContact.value) return contacts.value;
  return contacts.value.filter(contact => contact.id !== activeContact.value.id);
});

const noContactsMessage = computed(() => {
  if (!props.opportunityId) {
    return `Click the + button to associate Contacts with this ${props.type}`;
  }

  return `No Contacts associated with this ${props.type}`;
});

const noAccountsMessage = computed(() => {
  if (!props.opportunityId) {
    return `Click the + button to associate Accounts with this ${props.type}`;
  }

  return `No Accounts associated with this ${props.type}`;
});

const shouldDisableContactLabel = (label: ContactLabel) => {
  // If it isn't the primary or bill to label allow multiple.
  if (label.contactLabelId !== 1 && label.contactLabelId !== 3) {
    return false;
  }

  const usedLabels = contactsExcludingActive.value.flatMap(contact => contact.contactLabels);
  const idx = usedLabels?.findIndex(l => l?.contactLabelId === label.contactLabelId);
  return idx !== -1;
};

const canSaveChanges = computed(() => {
  if (props.type === 'Lead') return true;
  let primaryCount = 0;
  contacts.value?.forEach(contact => {
    contact.contactLabels?.forEach(label => {
      if (label.contactLabelDisplayName === 'Primary') {
        primaryCount += 1;
      }
    });
  });

  return primaryCount === 1;
});

const saveContactChangeButtonSeverity = computed(() => {
  if (!areContactsDirty.value) return 'secondary';
  if (!canSaveChanges.value) return 'danger';
  return 'primary';
});

const { isLoading, makeRequest } = useEndpoint<AssociationsResponse>({
  type: 'GET',
  successField: 'data',
  url: endpointUrl.value,
  startInLoadingState: Boolean(props.opportunityId),
  onSuccess: response => {
    accounts.value = response.data.accounts?.filter(account => account.c2Status !== 'Inactive'); // Filters out auto-generated accounts
    contacts.value = response.data.contacts;
    store.campaignAccounts = accounts.value;
    store.campaignContacts = contacts.value;

    emit('on:campaign-accounts-loaded', arrayHasValues(response.data.accounts));
    emit('on:campaign-contacts-loaded', arrayHasValues(response.data.contacts));
  },
  toast: { showOnSuccess: false },
});

const updateOpportunityEndpoint = useEndpoint<GenericApiResponse>({
  type: 'PUT',
  url: '/crm/opportunities',
  successField: 'success',
  toast: { showOnSuccess: false },
  onSuccess: () => {
    areContactsDirty.value = false;
    areAccountsDirty.value = false;
  },
});

const iconButtonPt = { root: { class: 'w-5 h-5 p-1 bg-transparent hover:bg-[var(--neutral-background)]' }};


const getContactTitle = (contact: AssociationContact) => {
  let title = '';
  if (!isEmptyString(contact.firstName)) {
    title = contact.firstName;
  }
  if (!isEmptyString(contact.lastName) && !isEmptyString(contact.firstName)) {
    title = `${title} ${contact.lastName}`;
  } else if (!isEmptyString(contact.lastName) && isEmptyString(contact.firstName)) {
    title = contact.lastName;
  }

  if (!isEmptyString(title)) {
    return title;
  }

  return 'Unnamed Contact';
};

const getContactSubtitle = (contact: AssociationContact) => {
  if (!isEmptyString(contact.jobTitle)) {
    return contact.jobTitle;
  }

  return contact.c2Name ?? ' ';
};

watch(
  () => props.opportunityId,
  (newCampaignId) => {
    if (newCampaignId) {
      makeRequest();
    }
  },
  { immediate: true },
);

const restoreAndCloseAccountSearchModal = () => {
  accounts.value = Object.values(accountsAtStartOfEditing.value);
  store.campaignAccounts = Object.values(accountsAtStartOfEditing.value);
  closeAccountSearchModal();
};

const restoreAndCloseContactSearchModal = () => {
  contacts.value = Object.values(contactsAtStartOfEditing.value);
  store.campaignContacts = Object.values(contactsAtStartOfEditing.value);
  closeContactSearchModal();
};

const closeContactSearchModal = () => {
  showAddContactModal.value = false;
  creationStore.resetState();
};

const closeAccountSearchModal = () => {
  showAssociateAccountsModal.value = false;
  creationStore.resetState();
};

const onAssociateContacts = () => {
  if (!creationStore.contacts?.length) return;

  contacts.value = creationStore.contacts;

  if (props.opportunityId) {
    areContactsDirty.value = true;
  }

  if (!canSaveChanges.value) {
    confirm.require({
      header: 'Notice',
      message: `There must be a primary contact associated with this ${props.type}.`,
      group: 'primaryContactSaveConfirm',
      acceptLabel: 'Ok',
      rejectClass: '!hidden',
    });
    return;
  }

  saveContactsAssociations();

  closeContactSearchModal();
};

const onAssociateAccount = () => {
  accounts.value = creationStore.accounts;

  saveAccountsAssociations();

  closeAccountSearchModal();
};

const onSaveContactClick = (e: MouseEvent) => {
  if (!canSaveChanges.value) {
    confirm.require({
      target: e.currentTarget as HTMLButtonElement,
      message: `There must be a primary contact associated with this ${props.type}.`,
      group: 'primaryContactConfirm',
      acceptLabel: 'Ok',
      rejectClass: 'hidden',
    });
    return;
  }

  saveContactsAssociations();
};

const saveContactsAssociations = async () => {
  const payload: OpportunityUpdatePayload = {
    data: {
      opportunity: [
        {
          id: props.opportunityId,
          contacts: contacts.value.map(contact => ({ id: contact.id, labelIds: contact.contactLabels?.map(label => label.contactLabelId) ?? []})),
        },
      ],
    },
  };

  await updateOpportunityEndpoint.makeRequest(payload);
};

const saveAccountsAssociations = async () => {
  const payload: OpportunityUpdatePayload = {
    data: {
      opportunity: [
        {
          id: props.opportunityId,
          billToId: accounts.value?.find(account => account.c2RawType === 'Bill-To')?.c2Id ?? accounts.value?.find(account => account.c2RawType === 'Agency')?.c2Id,
          agencyId: accounts.value?.find(account => account.c2RawType === 'Agency')?.c2Id,
          accountId: accounts.value?.find(account => account.c2RawType === 'Advertiser')?.c2Id ?? accounts.value?.find(account => account.c2RawType === 'Lead')?.c2Id,
        },
      ],
    },
  };

  await updateOpportunityEndpoint.makeRequest(payload);
};

const shouldDisableContactEmailButton = (contact: AssociationContact) => {
  if (!Array.isArray(contact?.addressEmails)) return true;
  return isEmptyString(contact.addressEmails[0]?.email);
};

const shouldDisableContactCallButton = (contact: AssociationContact) => {
  if (!Array.isArray(contact?.addressPhoneNumbers)) return true;
  return isEmptyString(contact.addressPhoneNumbers[0]?.number);
};

const onEmailContactClick = (contact: AssociationContact) => {
  if (!Array.isArray(contact.addressEmails)) return;
  const [record] = contact.addressEmails;
  if (isEmptyString(record?.email)) return;

  if (contact) {
    emit('post-activity', {
      modelObject: {
        id: contact.id,
        name: contact.fullName,
        view: 'contacts',
      },
      typeId: 1,
    });
  }

  window.open(`mailto:${record.email}`);
};

const onCallContactClick = (contact: AssociationContact) => {
  if (!Array.isArray(contact.addressPhoneNumbers)) return;
  const [record] = contact.addressPhoneNumbers;
  if (isEmptyString(record?.number)) return;

  if (contact) {
    emit('post-activity', {
      modelObject: {
        id: contact.id,
        name: contact.fullName,
        view: 'contacts',
      },
      typeId: 2,
    });
  }

  window.location.href = `tel:${record.number}`;
};

const onContactOptionsClick = (e: MouseEvent, contact: AssociationContact) => {
  activeContact.value = contact;
  contactMenuRef.value.toggle(e);
};

const creationStore = useOpportunityCreationStore();

const contactsAtStartOfEditing = ref();
const clickAddContact = () => {
  contactsAtStartOfEditing.value = cloneDeep(store.campaignContacts);
  creationStore.contacts = store.campaignContacts;
  showAddContactModal.value = true;
};

const accountsAtStartOfEditing = ref();
const clickAddAccount = () => {
  if (props.type === 'Opportunity') {
    store.createOpportunityContext = 'create-opportunity';
  } else {
    store.createOpportunityContext = 'create-lead';
  }
  accountsAtStartOfEditing.value = cloneDeep(store.campaignAccounts);
  creationStore.accounts = store.campaignAccounts;
  showAssociateAccountsModal.value = true;
};
const handleNavigateForward = (context: FlyoutNavigationContext) => {
  emit('navigate-forward', context);
};

</script>
<template>
  <div class="w-full flex flex-col gap-6">
    <FormSkeleton v-if="isLoading" />
    <template v-else>
      <Section class="mb-3" :footer="false">
        <AssociationListContainer
          title="Contacts"
          :item-count="contacts.length"
          :no-items-message="noContactsMessage"
          @on:add-click="clickAddContact"
        >
          <AssociationListItem
            v-for="contact in contacts"
            :id="contact.id"
            :key="contact.id"
            :tab-id="props.tabId"
            association-type="contact"
            :title="getContactTitle(contact)"
            :subtitle="getContactSubtitle(contact)"
            :border="true"
            @navigate-forward="handleNavigateForward"
          >
            <div class="flex gap-3 items-center justify-end">
              <Button
                v-if="props.context === 'editing'"
                severity="secondary"
                icon="pi pi-envelope"
                size="small"
                text
                rounded
                :pt="iconButtonPt"
                :disabled="shouldDisableContactEmailButton(contact)"
                @click="onEmailContactClick(contact)"
              />
              <Button
                v-if="props.context === 'editing'"
                severity="secondary"
                icon="pi pi-phone"
                size="small"
                class="md:hidden"
                text
                rounded
                :pt="iconButtonPt"
                :disabled="shouldDisableContactCallButton(contact)"
                @click="onCallContactClick(contact)"
              />
              <Button
                severity="secondary"
                icon="pi pi-ellipsis-v"
                size="small"
                text
                rounded
                :pt="iconButtonPt"
                @click="e => onContactOptionsClick(e, contact)"
              />
            </div>
            <template #footer>
              <div
                class="w-full flex items-center gap-2"
                :class="[contact.contactLabels?.length > 0 ? 'pb-2 md:pb-4': '']"
              >
                <ContactTypeChip v-for="label in contact.contactLabels" :key="label.contactLabelId" :label="label" />
              </div>
            </template>
          </AssociationListItem>
          <div v-if="props.context === 'editing'" class="flex items-center">
            <Button
              label="Save"
              size="small"
              :severity="saveContactChangeButtonSeverity"
              :disabled="!areContactsDirty"
              :dt="greenButtonTheme"
              @click="onSaveContactClick"
            />
          </div>
        </AssociationListContainer>
      </Section>

      <slot name="newContactForm" />

      <Section class="mb-3" :footer="false">
        <AssociationListContainer
          title="Accounts"
          :no-items-message="noAccountsMessage"
          :disable-add-button="!canAssociateMoreAccounts"
          :item-count="accounts.length"
          @on:add-click="clickAddAccount"
        >
          <AssociationListItem
            v-for="account in accounts"
            :id="account.c2Id"
            :key="account.c2Id"
            :tab-id="props.tabId"
            association-type="account"
            :title="account.c2Name"
            :subtitle="account.c2Type"
            :border="true"
            @navigate-forward="handleNavigateForward"
          >
            <div class="flex gap-3 items-center justify-end">
              <ContactTypeChip
                :label="{contactLabelId: 0, contactLabelDisplayName: account.c2Type as ContactLabelType}"
              />
              <AccountLabelTag :label="account.accountLabel" />
            </div>
            <template #footer>
              <div class="w-full flex items-center gap-2" />
            </template>
          </AssociationListItem>
        </AssociationListContainer>
      </Section>
    </template>
  </div>
  <Modal
    :visible="showAddContactModal"
    title="Associate Contact"
    accept-label="Save"
    @close="restoreAndCloseContactSearchModal"
    @accept="onAssociateContacts"
  >
    <template #body>
      <div class="p-4">
        <Contacts :address="accounts?.[0]?.addresses[0]" :company-name="accounts?.[0]?.c2Name" />
      </div>
    </template>
  </Modal>
  <Modal
    :visible="showAssociateAccountsModal"
    title="Associate Account"
    accept-label="Save"
    :can-click-accept="Boolean(creationStore.errorBag.accounts.length === 0)"
    @close="restoreAndCloseAccountSearchModal"
    @accept="onAssociateAccount"
  >
    <template #body>
      <div class="p-4">
        <Accounts :industry-code-required="industryCodeRequired" />
      </div>
    </template>
  </Modal>
  <Menu ref="contactMenuRef" :model="contactMenuItems" :popup="true" />
  <ConfirmDialog group="removeContactConfirm" />
  <ConfirmDialog group="primaryContactSaveConfirm" />
  <ConfirmDialog group="primaryContactConfirm" />
  <Dialog v-model:visible="showContactTypeSelectDialog" modal header="Edit contact types">
    <Listbox
      v-model="activeContact.contactLabels"
      multiple
      :options="store.options[props.type].contactLabels"
      :option-disabled="shouldDisableContactLabel"
      option-label="contactLabelDisplayName"
      class="w-full"
      @update:model-value="areContactsDirty = true"
    />
  </Dialog>
</template>
